import '@toasttab/main/src/assets/styles/global.css'
import 'lazysizes'
import React, { Suspense } from 'react'
import RootElement from './src/components/root-element'
import { wrapPageElement as SharedWrapPageElement } from '@toasttab/shared/src/page/wrapPageElement'
import { sha256encode } from '@toasttab/shared/src/utils/dataCaptureUtils'
import _helpers from '@toasttab/shared/src/globals/_helpers'

export const disableCorePrefetching = () => true

export const wrapRootElement = ({ element }: any) => {
  const enableContentfulLivePreview =
    process.env.GATSBY_CONTENTFUL_LIVE_PREVIEW_ENABLE !== 'false'

  // Get locale from query string (only runs in browser)
  let locale = 'en'
  if (typeof window !== 'undefined') {
    const params = new URLSearchParams(window.location.search)
    locale = params.get('locale') || 'en'
  }

  if (enableContentfulLivePreview) {
    // If deployed to a preview site, load the ContentfulLivePreviewProvider bundle
    const ContentfulLivePreviewProvider = React.lazy(() =>
      import(
        /* webpackChunkName: "vendor-contentful-live-preview" */
        '@contentful/live-preview/react'
      ).then((m) => ({
        default: m.ContentfulLivePreviewProvider
      }))
    )
    const App = () => (
      <Suspense fallback={null}>
        <ContentfulLivePreviewProvider
          locale={String(locale)}
          enableInspectorMode={true}
          enableLiveUpdates={true}
          debugMode={false}
        >
          <RootElement>{element}</RootElement>
        </ContentfulLivePreviewProvider>
      </Suspense>
    )
    return <App />
  } else {
    // If not running on a preview site, don't load the ContentfulLivePreviewProvider bundle
    const App = () => <RootElement>{element}</RootElement>
    return <App />
  }
}

export const wrapPageElement = SharedWrapPageElement

export const onClientEntry = () => {
  if (typeof window === 'undefined') return // Ensure this runs only in the browser

  if (window.Fides?.initialized) {
    // To make sure this script loads if `FidesInitialized` was called
    // before your script loader runs.
    conditionallyLoadScripts()
  } else {
    // To make sure the script loads if your script loader ran before
    // Fides was initialized.
    addEventListener('FidesInitialized', conditionallyLoadScripts)
  }

  // If the user is opted out, and then opts in, make sure to run your script loader.
  addEventListener('FidesUpdated', conditionallyLoadScripts)

  // @ts-ignore
  window.dataLayer = window.dataLayer || []

  const initGTMOnEvent = (event: any) => {
    console.log('initGTMOnEvent')
    initGTM()
    event.currentTarget.removeEventListener(event.type, initGTMOnEvent) // Remove the triggered event listener
  }

  const initGTM = () => {
    // @ts-ignore
    if (window.gtmDidInit) {
      return false // Ensure the script is not added multiple times
    }
    // @ts-ignore
    window.gtmDidInit = true

    // Create and append the GTM script
    const script = document.createElement('script')
    script.type = 'text/javascript'
    script.async = true
    script.src = 'https://www.googletagmanager.com/gtm.js?id=GTM-MNHK89W'

    script.onload = () => {
      // @ts-ignore
      window.dataLayer.push({
        event: 'gtm.js',
        'gtm.start': new Date().getTime(),
        'gtm.uniqueEventId': 0
      }) // Ensure PageViews are tracked
    }

    // MKE-3149: Add SHA256 hashed email and phone number to dataLayer for GTM tracking pixel
    if (_helpers.getCookie('email') || _helpers.getCookie('phonenumber')) {
      Promise.all([
        sha256encode(_helpers.getCookie('email')),
        sha256encode(_helpers.getCookie('phonenumber'))
      ]).then(([encodedEmail, encodedPhone]) => {
        // @ts-ignore
        window.dataLayer.push({
          user_data: {
            sha256_email_address: encodedEmail,
            sha256_phone_number: encodedPhone
          }
        })
      })
    }

    document.head.appendChild(script)
  }

  // Add event listeners
  document.addEventListener('DOMContentLoaded', () => {
    setTimeout(initGTM, 3500) // Initialize GTM after 3500ms
  })

  document.addEventListener('scroll', initGTMOnEvent)
  document.addEventListener('mousemove', initGTMOnEvent)
  document.addEventListener('touchstart', initGTMOnEvent)
}

function conditionallyLoadScripts() {
  // Check the relevant consent flags
  if (window.Fides?.consent?.analytics) {
    // Load script(s)
    loadHeapAnalytics()
    loadDataDog()
  }

  if (window.Fides?.consent?.marketing) {
    loadImpactRadiusTracking()
  }
}

function jsonStringifySafe(obj: Record<string, unknown>) {
  return JSON.stringify(obj)
    .replace(/</g, '\\u003c')
    .replace(/<\/script/g, '<\\/script')
}

function getDataDogEnvironment() {
  const url = process.env.GATSBY_CMS_URL
    ? process.env.GATSBY_CMS_URL.replace(/(^\w+:|^)\/\//, '')
    : 'pos.toasttab.com'

  switch (true) {
    case url.includes('pos'):
      return 'production'
    case url.includes('staging'):
      return 'staging'
    case url.includes('dev'):
      return 'development'
    default:
      return 'development'
  }
}

function loadDataDog() {
  if (!window.Fides?.consent?.analytics) {
    // @ts-ignore
    if (!!window?.DD_RUM) {
      // @ts-ignore
      window.DD_RUM.setTrackingConsent(getDataDogTrackingConsent())
    }
    return
  }

  if (!!window?.DD_RUM) {
    return
  }

  loadScript(
    'https://www.datadoghq-browser-agent.com/us1/v5/datadog-rum.js',
    () => {
      // @ts-ignore
      window.DD_RUM.onReady(function () {
        const env = getDataDogEnvironment()
        // @ts-ignore
        window.DD_RUM.setTrackingConsent(getDataDogTrackingConsent())
        // @ts-ignore
        window.DD_RUM.init(
          `${jsonStringifySafe({
            clientToken: process.env.DATADOG_CLIENT_TOKEN || '',
            applicationId: process.env.DATADOG_APP_ID || '',
            service: 'mktg-toasttab-com-headless',
            env,
            site: 'datadoghq.com',
            sessionSampleRate: 1,
            sessionReplaySampleRate: 0, // if not included, the default is 100
            trackResources: true,
            trackLongTasks: true,
            trackUserInteractions: true,
            enablePrivacyForActionName: true
          })}`
        )
      })
    }
  )
}

function getDataDogTrackingConsent() {
  return window.Fides?.consent?.analytics ? 'granted' : 'not-granted'
}

function loadHeapAnalytics() {
  if (!window.Fides?.consent?.analytics) {
    if (!!window?.heap) {
      window.heap.stopTracking()
      window.heap.clearUserProperties()
      window.heap.resetIdentity()
    }
    return
  }

  if (!!window?.heap) {
    return
  }

  const appId =
    process.env.BRANCH_NAME && process.env.BRANCH_NAME.includes('main')
      ? '1227582568'
      : '4031532897'

  // @ts-ignore
  ;(window.heap = window.heap || []),
    // @ts-ignore
    (window.heapReadyCb = window.heapReadyCb || []),
    (window.heap = window.heap || []),
    // @ts-ignore
    (heap.load = function (e, t) {
      ;(window.heap.envId = e),
        (window.heap.clientConfig = t = t || {}),
        (window.heap.clientConfig.shouldFetchServerConfig = !1)
      var a = document.createElement('script')
      ;(a.type = 'text/javascript'),
        (a.async = !0),
        (a.src = 'https://cdn.us.heap-api.com/config/' + e + '/heap_config.js')
      var r = document.getElementsByTagName('script')[0]
      r.parentNode.insertBefore(a, r)
      var n = [
          'init',
          'startTracking',
          'stopTracking',
          'track',
          'resetIdentity',
          'identify',
          'getSessionId',
          'getUserId',
          'getIdentity',
          'addUserProperties',
          'addEventProperties',
          'removeEventProperty',
          'clearEventProperties',
          'addAccountProperties',
          'addAdapter',
          'addTransformer',
          'addTransformerFn',
          'onReady',
          'addPageviewProperties',
          'removePageviewProperty',
          'clearPageviewProperties',
          'trackPageview'
        ],
        i = function (e: string) {
          return function () {
            var t = Array.prototype.slice.call(arguments, 0)
            // @ts-ignore
            window.heapReadyCb.push({
              name: e,
              fn: function () {
                // @ts-ignore
                heap[e] && heap[e].apply(heap, t)
              }
            })
          }
        }
      // @ts-ignore
      for (var p = 0; p < n.length; p++) heap[n[p]] = i(n[p])
    })

  // Initialize the library with your `appId`.
  // @ts-ignore
  heap.load(appId)
}

function loadImpactRadiusTracking() {
  if (typeof window.ire === 'function') {
    // already loaded, so don't load again
    return
  }

  loadScript(
    'https://utt.impactcdn.com/A2526552-22b7-4399-83ee-06f98f5f67921.js',
    () => {
      window?.ire?.('identify', {
        customerid: '',
        customeremail: ''
      })
    }
  )
}

function loadScript(src: string, callback?: () => void) {
  const script = document.createElement('script')
  script.src = src
  script.async = true
  document.head.appendChild(script)

  if (callback) {
    script.onload = callback
  }
}
